import { EligibilityStatus, ProductEnrollment } from '@/gql/sales-api/graphql';
import { getFullStorySessionURL } from '@/utils/fullStory-utils';
import { isClient } from '@/utils/isClient';
import { EventProperties } from '@asurion-core/analytics-core';
import {
  Billing,
  Customer,
  CustomerSession,
  ProductEligibilityStatus,
  ProductEnrollmentStatus,
  ProductOffer,
} from '@asurion-core/asp-core';
import { PartialDeep } from 'type-fest';
import UserBrowser from './UserBrowser';
import {
  IdentifySegmentUserInput,
  IdentifySegmentUserPayload,
  NonSegmentEvents,
  SegmentEvents,
  TrackSegmentEcommerceEventInput,
  TrackSegmentEcommerceEventPayload,
} from './types';

export type EventPropertiesInput = {
  billing?: PartialDeep<Billing> | null;
  customer?: PartialDeep<Customer> | null;
  customerSession?: PartialDeep<Omit<CustomerSession, '__typename'>> | null;
  productOffer?:
    | PartialDeep<Omit<ProductOffer, '__typename' | 'expertSession'>>
    | {
        id?: string;
        productEligibility: {
          status: EligibilityStatus | ProductEligibilityStatus | undefined;
        };
        productSku?: string;
        productEnrollment?:
          | {
              id: string;
              status: ProductEnrollmentStatus;
              planId: string;
            }
          | ProductEnrollment;
      }
    | null;
  event: SegmentEvents | NonSegmentEvents;
  extraData?: Record<string, unknown>;
  metaData?: Record<string, unknown>;
  identities?: Record<string, unknown>;
  products?: {
    id: string;
    productId: string;
    sku: string;
    category?: string;
    name?: string;
    brand?: string;
    price?: string;
    quantity: number;
    coupon?: string;
  }[];
};
/*
  Builds the event properties object that is sent to the analytics services.
  This function pulls data from the UserBrowser session and also from the payload that is passed in to build an event payload that matches what is expected by EIP.
*/
export const buildEventProperties = (
  properties: EventPropertiesInput,
): EventProperties => {
  const browserSession = isClient() ? UserBrowser.get() : undefined;

  return {
    Name: properties.event,
    Scope: 'd2c-sales-tool',
    Time: new Date().toISOString(),
    ExtraData: {
      CustomerSessionId: properties.customerSession?.id,
      Email: properties.customer?.email,
      Customer: {
        id: properties.customer?.id ?? '',
        firstName: properties.customer?.firstName,
        lastName: properties.customer?.lastName,
        email: properties.customer?.email,
        mdn: properties.customer?.mdn,
      },
      FullstorySession: getFullStorySessionURL(),
      ProductOffer: properties.productOffer
        ? {
            id: properties.productOffer?.id ?? '',
            productSku: properties.productOffer.productSku
              ? properties.productOffer.productSku
              : (browserSession?.ProductSku ?? ''),
            productEligibility: properties.productOffer?.productEligibility
              ? {
                  status: properties.productOffer.productEligibility
                    .status as ProductEligibilityStatus,
                }
              : undefined,
            productEnrollment: properties.productOffer?.productEnrollment
              ? {
                  id: properties.productOffer.productEnrollment.id ?? '',
                  status:
                    (properties.productOffer.productEnrollment
                      .status as ProductEnrollmentStatus) ?? undefined,
                  planId:
                    properties.productOffer.productEnrollment.planId ??
                    undefined,
                }
              : undefined,
          }
        : undefined,
      products: properties.products ?? [
        {
          id:
            browserSession?.sessionQueryParameters?.productSku ??
            browserSession?.ProductSku,
          productId:
            browserSession?.sessionQueryParameters?.productSku ??
            browserSession?.ProductSku,
          sku:
            browserSession?.sessionQueryParameters?.productSku ??
            browserSession?.ProductSku,
          category: browserSession?.ProductConfig?.productType,
          name: browserSession?.ProductConfig?.name,
          brand: UserBrowser.Partner ?? 'Asurion',
          price: browserSession?.ProductConfig?.price,
          quantity: 1,
          coupon: browserSession?.sessionQueryParameters?.coupon,
        },
      ],
      ...browserSession?.sessionQueryParameters,
      ...properties.extraData,
      Referrer: isClient() ? document.referrer : '',
      GoogleClientId: isClient() ? window.gaGlobal?.vid : '',
    },
    Identities: {
      UserBrowserId: browserSession?.UserBrowserId,
      ...properties.identities,
    },
    MetaData: {
      HostApp: 'd2c-sales-tool',
      SourceApp: 'd2c-sales-tool',
      Client: UserBrowser.Partner,
      ...properties.metaData,
    },
  };
};

export const buildSegmentIdentifyPayload = ({
  customer: { email, firstName, lastName, mdn },
  customerSession: { id },
}: IdentifySegmentUserInput): IdentifySegmentUserPayload => {
  const browserSession = UserBrowser.get();
  return {
    userId: email,
    traits: {
      firstName,
      lastName,
      correlationId: id,
      isEnrolled: 0,
      mdn,
      productSku:
        browserSession?.sessionQueryParameters?.productSku ??
        browserSession?.ProductSku,
    },
  };
};

export const buildSegmentEcommerceEventPayload = ({
  eventName,
  email,
  productOfferId,
  step,
  affiliation,
  tax,
  customerSessionId,
  paymentMethod = 'Credit Card',
  isEnrolled = 0,
}: TrackSegmentEcommerceEventInput): TrackSegmentEcommerceEventPayload => {
  const browserSession = isClient() ? UserBrowser.get() : undefined;
  const campaign = {
    name: browserSession?.sessionQueryParameters?.utm_campaign,
    source: browserSession?.sessionQueryParameters?.utm_source,
    medium: browserSession?.sessionQueryParameters?.utm_medium,
    term: browserSession?.sessionQueryParameters?.utm_term,
  };
  const productConfig = browserSession?.ProductConfig;
  const price = productConfig ? parseFloat(productConfig.price) : 0;
  return {
    eventName: eventName,
    userId: email,
    properties: {
      correlationId: customerSessionId,
      affiliation: affiliation ?? 'DigitalEnrollment',
      orderId: productOfferId ?? '',
      total: price + (tax ?? 0),
      isEnrolled,
      products: [
        {
          id:
            browserSession?.sessionQueryParameters?.productSku ??
            browserSession?.ProductSku,
          productId:
            browserSession?.sessionQueryParameters?.productSku ??
            browserSession?.ProductSku,
          sku:
            browserSession?.sessionQueryParameters?.productSku ??
            browserSession?.ProductSku,
          category: productConfig?.productType,
          name: productConfig?.name,
          brand: UserBrowser.Partner ?? 'Asurion',
          price,
          quantity: 1,
          coupon: browserSession?.sessionQueryParameters?.coupon,
        },
      ],
      currency: 'USD',
      pageUrl: isClient() ? window.location.href : '',
      productSku:
        browserSession?.sessionQueryParameters?.productSku ??
        browserSession?.ProductSku,
      tax,
      discount: 0,
      coupon: browserSession?.sessionQueryParameters?.coupon,
      step,
      paymentMethod: paymentMethod,
    },
    integrations: {
      'Google Analytics': isClient() ? window.location.href : '',
    },
    context: {
      campaign,
    },
  };
};
